export const FEATURE_FLAG_KEYS = [
  'booking',
  'commissions',
  'analytics',
  'digitalPresence',
  'mcpWorldviaRag',
  'threadReplayFeedback',
  'agenticAdminAdvanced',
  'invoicingMvp',
  'unclaimedCommission',
  'tramsHistoryImport',
  'profileMonthlyUsage',
  'profileNotifications',
  'profileDefaultAiModel',
  // Parent switch for the whole onboarding feature (AR-171), including the
  // public member checkout at /checkout. Defined on the backend too — the
  // resolved value from /api/feature-flags/me is what gates the admin surfaces.
  'onboarding',
  // PRO migration (AR-172). `proMigration` is the single parent switch for the
  // whole migration; every sub-flag below is additionally gated by it, so each
  // piece can ship on its own and the set retires together.
  'proMigration',
  'proMigrationAccountCenter',
  'proMigrationAccountData',
  'proMigrationSettings',
  'proMigrationPhase1',
  // Threads on AgentCore. Resolved per user by the backend (/api/feature-flags/me);
  // the build-time default stays off so nobody leaves the existing Threads engine by accident.
  'agentcoreThreads',
  // AIVIA's CRM tools (trips, clients, linking). Off until CRM launches; resolved by the backend.
  'agentcoreCrmTools',
] as const;

type FeatureFlagKeyTuple = typeof FEATURE_FLAG_KEYS;
export type FeatureFlagKey = FeatureFlagKeyTuple[number];
export type FeatureFlags = Record<FeatureFlagKey, boolean>;

type RuntimeFlagsSource = Partial<Record<FeatureFlagKey | string, unknown>> & {
  __AIVIA_FEATURE_FLAGS__?: RuntimeFlagsSource;
  __AIVIA_FLAGS__?: RuntimeFlagsSource;
};

const env = ((import.meta as ImportMeta & { env?: Record<string, unknown> }).env) ?? {};

const readEnvValue = (key: string): unknown => {
  return Object.prototype.hasOwnProperty.call(env, key) ? env[key] : undefined;
};

const parseBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'on', 'yes', 'enabled'].includes(normalized)) return true;
    if (['0', 'false', 'off', 'no', 'disabled'].includes(normalized)) return false;
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// Base flag definitions
// ---------------------------------------------------------------------------

const baseFlags: FeatureFlags = {
  booking: parseBoolean(readEnvValue('VITE_FEATURE_BOOKING'), false),
  commissions: parseBoolean(
    readEnvValue('VITE_FEATURE_COMMISSIONS') ?? readEnvValue('VITE_FEATURE_PAYMENTS'),
    false,
  ),
  analytics: parseBoolean(readEnvValue('VITE_FEATURE_ANALYTICS'), false),
  digitalPresence: parseBoolean(readEnvValue('VITE_FEATURE_DIGITAL_PRESENCE'), false),
  mcpWorldviaRag: parseBoolean(readEnvValue('VITE_FEATURE_MCP_WORLDVIA_RAG'), true),
  threadReplayFeedback: parseBoolean(readEnvValue('VITE_FEATURE_THREAD_REPLAY_FEEDBACK'), false),
  // Gates advanced/internal admin surfaces (e.g., Orchestration Rollout console)
  // Requires SYSTEM_ADMIN role in addition to this flag being ON
  agenticAdminAdvanced: parseBoolean(readEnvValue('VITE_FEATURE_AGENTIC_ADMIN_ADVANCED'), false),
  invoicingMvp: parseBoolean(readEnvValue('VITE_FEATURE_INVOICING_MVP'), false),
  unclaimedCommission: parseBoolean(readEnvValue('VITE_FEATURE_UNCLAIMED_COMMISSION'), false),
  tramsHistoryImport: parseBoolean(
    readEnvValue('VITE_FEATURE_TRAMS_HISTORY_IMPORT') ?? readEnvValue('VITE_ENABLE_TRAMS_HISTORY_IMPORT'),
    false,
  ),
  // Profile navigation surfaces hidden until the underlying features ship (AR-291).
  // Flip the matching VITE_FEATURE_* env var (or set window.__AIVIA_FEATURE_FLAGS__)
  // to re-enable — no code change required.
  profileMonthlyUsage: parseBoolean(readEnvValue('VITE_FEATURE_PROFILE_MONTHLY_USAGE'), false),
  profileNotifications: parseBoolean(readEnvValue('VITE_FEATURE_PROFILE_NOTIFICATIONS'), false),
  profileDefaultAiModel: parseBoolean(readEnvValue('VITE_FEATURE_PROFILE_DEFAULT_AI_MODEL'), false),
  // Onboarding (AR-171) — off by default in every environment, including
  // staging. Admin surfaces resolve it per user from the backend; the anonymous
  // /checkout route gates on this build-time value — set VITE_FEATURE_ONBOARDING
  // to switch it on.
  onboarding: parseBoolean(readEnvValue('VITE_FEATURE_ONBOARDING'), false),
  // PRO migration (AR-172) — the parent switch and its sub-flags, all off by
  // default in every environment. A surface needs the parent AND its own
  // sub-flag, so the Account center can ship without the data binding, and the
  // Settings module without either.
  proMigration: parseBoolean(readEnvValue('VITE_FEATURE_PRO_MIGRATION'), false),
  proMigrationAccountCenter: parseBoolean(
    readEnvValue('VITE_FEATURE_PRO_MIGRATION_ACCOUNT_CENTER'),
    false,
  ),
  proMigrationAccountData: parseBoolean(
    readEnvValue('VITE_FEATURE_PRO_MIGRATION_ACCOUNT_DATA'),
    false,
  ),
  proMigrationSettings: parseBoolean(
    readEnvValue('VITE_FEATURE_PRO_MIGRATION_SETTINGS'),
    false,
  ),
  // AR-445 — the Phase-1 navigation and the screens it reaches.
  proMigrationPhase1: parseBoolean(
    readEnvValue('VITE_FEATURE_PRO_MIGRATION_PHASE1'),
    false,
  ),
  agentcoreThreads: false,
  agentcoreCrmTools: false,
};

const readRuntimeOverrides = (): Partial<FeatureFlags> => {
  if (typeof globalThis === 'undefined') return {};
  const globalScope = globalThis as RuntimeFlagsSource;
  const overrides = globalScope.__AIVIA_FEATURE_FLAGS__ ?? globalScope.__AIVIA_FLAGS__;
  if (!overrides || typeof overrides !== 'object') return {};

  const resolved: Partial<FeatureFlags> = {};
  for (const key of FEATURE_FLAG_KEYS) {
    if (key in overrides) {
      resolved[key] = parseBoolean((overrides as RuntimeFlagsSource)[key], baseFlags[key]);
    }
  }
  return resolved;
};

let localOverrides: Partial<FeatureFlags> = {};

export const setFeatureFlagOverrides = (overrides?: Partial<FeatureFlags>) => {
  localOverrides = overrides ? { ...overrides } : {};
};

export const getFeatureFlags = (): FeatureFlags => {
  const runtimeOverrides = readRuntimeOverrides();

  return FEATURE_FLAG_KEYS.reduce((acc, key) => {
    const localValue = localOverrides[key];
    if (typeof localValue === 'boolean') {
      acc[key] = localValue;
      return acc;
    }

    const runtimeValue = runtimeOverrides[key];
    if (typeof runtimeValue === 'boolean') {
      acc[key] = runtimeValue;
      return acc;
    }

    acc[key] = baseFlags[key];
    return acc;
  }, {} as FeatureFlags);
};

export const isFeatureEnabled = (flag: FeatureFlagKey): boolean => getFeatureFlags()[flag];

/**
 * For rollout-aware evaluation (percentage-based rollouts), use the
 * `useResolvedFeatureFlags` hook instead. It fetches the resolved flag
 * states from the backend (`GET /api/feature-flags/me`), which is the
 * single source of truth for rollout percentages, scoped access, and
 * base flag state.
 *
 * Example:
 *   import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
 *   const { isEnabled } = useResolvedFeatureFlags();
 *   if (isEnabled('booking')) { ... }
 *
 * The `isFeatureEnabled()` / `getFeatureFlags()` functions above remain
 * useful for simple boolean flags that don't use rollout percentages
 * (e.g. build-time VITE_FEATURE_X=true/false).
 */
